import { useState } from 'react';
import { SMSNumberV1 } from '@frontend/api-sms-number';
import { Divider } from '@frontend/divider';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { formatPhoneNumber, formatPhoneNumberE164 } from '@frontend/phone-numbers';
import { useAppScopeStore } from '@frontend/scope';
import { useAppFlagStore } from '@frontend/shared';
import type { PersonPhoneSelection, ThreadHeaderSize } from '@frontend/thread-header-context';
import type { CommonHTMLAttributes } from '@frontend/types';
import { theme } from '@frontend/theme';
import { Button, PopoverMenu, PopoverMenuItem, styles, Text, usePopoverMenu } from '@frontend/design-system';
import { CustomPhoneInputField } from './custom-phone-input-field';
import { PersonPhoneSelectorItem } from './person-phone-selector-item';

export type PersonPhoneSelectorState = {
  showButton: boolean;
  currentSelectedPhone: SMSNumberV1.Types.PhoneValidity;
  currentSelectedPhoneLabel: string;
  validPhones: SMSNumberV1.Types.PhoneValidity[];
  allPhones: SMSNumberV1.Types.PhoneValidity[];
  isCurrentSelectedNumberCustom?: boolean;
  allowCustomPhoneNumber?: boolean;
  popoverState: ReturnType<typeof usePopoverMenu<HTMLElement>>;
  locationId: string;
  personId?: string;
};

export type PersonPhoneSelectorProps = PersonPhoneSelectorState & {
  color?: 'default' | 'white';
  onSelectPersonPhone: (selection: PersonPhoneSelection, threadId?: string) => void;
  variant?: ThreadHeaderSize;
} & CommonHTMLAttributes;

export const PersonPhoneSelector = ({
  showButton,
  currentSelectedPhone,
  currentSelectedPhoneLabel,
  validPhones,
  allPhones: _,
  popoverState,
  color = 'default',
  onSelectPersonPhone,
  isCurrentSelectedNumberCustom,
  allowCustomPhoneNumber,
  locationId,
  variant = 'normal',
  personId,
  ...rest
}: PersonPhoneSelectorProps) => {
  const { t } = useTranslation('thread-header');

  const [showCustomPhoneInput, setShowCustomPhoneInput] = useState<boolean>(!!isCurrentSelectedNumberCustom);

  const showPhoneDropdownButton = (showButton || allowCustomPhoneNumber) && !!validPhones.length;

  const handleCustomNumberChange = (newCustomNumber: string) => {
    const isNewCustomNumberValid = newCustomNumber.length === 10;
    if (currentSelectedPhone.number !== newCustomNumber)
      onSelectPersonPhone({ personPhone: isNewCustomNumberValid ? newCustomNumber : '', personId });
  };

  const { selectedLocationIds } = useAppScopeStore();
  const { getFeatureFlagValue } = useAppFlagStore();
  const shouldUseV4 = getFeatureFlagValue({
    locationIds: selectedLocationIds,
    flagName: 'phone-numbers-name-id',
    strategy: 'ALL',
  });
  const formattedPhoneNumber = formatPhoneNumber(currentSelectedPhone.number);

  return (
    <>
      <span
        css={{
          display: 'flex',
          alignItems: 'center',
          gap: shouldUseV4
            ? theme.spacing(0.25)
            : theme.spacing(showCustomPhoneInput || !showPhoneDropdownButton ? 0.5 : 0),
          justifyContent: 'flex-start',
          overflow: 'hidden',
        }}
        {...rest}
      >
        {/* TODO: remove once `phone-numbers-name-id` FF is deleted */}
        {!shouldUseV4 &&
          (variant === 'small' ? (
            <Icon name='arrow-right-small' color='light' css={{ rotate: '-45deg' }} />
          ) : (
            <Text
              color={color === 'white' ? 'white' : 'light'}
              size='medium'
              weight={color === 'white' ? 'bold' : undefined}
            >
              {t('To:')}
            </Text>
          ))}
        {/* END OF TODO */}

        {shouldUseV4 && variant === 'normal' && (
          <Text color='subdued' size='small' weight={color === 'white' ? 'bold' : undefined}>
            {t('To:')}
          </Text>
        )}
        {shouldUseV4 && (
          <Icon name='cell-phone-small' color='subdued' height={12} width={12} css={{ fontWeight: 'normal' }} />
        )}
        {showCustomPhoneInput ? (
          <CustomPhoneInputField
            onCustomNumberChange={handleCustomNumberChange}
            initValue={isCurrentSelectedNumberCustom ? currentSelectedPhone.number : ''}
            iconColor={color}
            popoverState={popoverState}
          />
        ) : showPhoneDropdownButton ? (
          <Button
            variant='tertiary'
            css={[
              {
                display: 'flex',
                alignItems: 'center',
                gap: theme.spacing(0.5),
                color: color === 'white' ? theme.colors.white : theme.colors.primary50,
                fontWeight: shouldUseV4 ? 'bold' : 'normal',
                padding: theme.spacing(1, 0.75),
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              },
              shouldUseV4
                ? {
                    height: 24,
                    padding: theme.spacing(0.375, 0.25),
                    '&:hover, &:active:not([aria-disabled="true"])': {
                      background: theme.colors.neutral5,
                    },
                  }
                : color === 'white'
                ? {
                    ':hover, :focus': {
                      backgroundColor: theme.colors.primary60,
                      borderColor: 'transparent',
                    },
                  }
                : {},
            ]}
            hoverLabel={shouldUseV4 ? formattedPhoneNumber : currentSelectedPhoneLabel}
            {...popoverState.getTriggerProps()}
          >
            <Text size={shouldUseV4 ? 'small' : 'medium'} css={styles.truncate}>
              {shouldUseV4 ? formattedPhoneNumber : currentSelectedPhoneLabel}
            </Text>
            <Icon
              name='caret-down-tiny'
              color={color === 'white' ? 'white' : 'light'}
              css={[
                { transition: 'rotate 200ms ease-in-out' },
                popoverState.isOpen && { rotate: '-180deg' },
                { minWidth: 'min-content' },
              ]}
              height={8}
              width={8}
              fontWeight={800}
            />
          </Button>
        ) : (
          <Text
            size={shouldUseV4 ? 'small' : 'medium'}
            color={shouldUseV4 ? 'light' : color === 'white' ? 'white' : 'default'}
            css={{ lineHeight: 1, padding: theme.spacing(1, 0.5), ...styles.truncate }}
          >
            {shouldUseV4 ? formattedPhoneNumber : currentSelectedPhoneLabel}
          </Text>
        )}
      </span>
      {showPhoneDropdownButton && (
        <PopoverMenu {...popoverState.getMenuProps()}>
          {validPhones.map(({ number, label }, index) => {
            const active = formatPhoneNumberE164(number) === formatPhoneNumberE164(currentSelectedPhone.number);
            return (
              <PersonPhoneSelectorItem
                key={`${label}-${number}-${index}`}
                {...popoverState.getItemProps({
                  index,
                  onClick: () => {
                    setShowCustomPhoneInput(false);
                    onSelectPersonPhone({ personPhone: number, personId });
                  },
                })}
                active={active}
                personPhone={number}
                phoneLabel={label}
                locationId={locationId}
                data-testid='person-phone-selector-item'
              />
            );
          })}
          {allowCustomPhoneNumber && (
            <>
              {/* TODO: replace hardcoded value */}
              <Divider margin={0} css={{ width: showCustomPhoneInput ? '218px' : 'auto' }} />
              <PopoverMenuItem
                key='custom-phone'
                {...popoverState.getItemProps({
                  index: validPhones.length,
                  onClick: () => setShowCustomPhoneInput(true),
                })}
                active={showCustomPhoneInput}
              >
                <Text color='primary'> {t('Custom Number')} </Text>
              </PopoverMenuItem>
            </>
          )}
        </PopoverMenu>
      )}
    </>
  );
};
